import { FC, MouseEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, BadgeProps, Box, IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import {
	Logout as LogoutIcon,
	KeyboardArrowDown as KeyboardArrowDownIcon,
	KeyboardArrowUp as KeyboardArrowUpIcon,
	ManageAccounts as ManageAccountsIcon,
	Key as KeyIcon,
	Notifications as NotificationsIcon
} from '@mui/icons-material';
import moment from 'moment';
import { styled } from '@mui/material/styles';
// Functions/Constants/Enum
import { Modal } from 'shared/constants/enum';
import { calculateDayDifference, checkTimeline, createAction } from 'shared/util/Utility';
import { INITIAL_MODAL_STATE, ROUTES } from 'shared/constants/constant';
import * as actionTypes from 'store/action-types';
import { colors } from 'assets/styles/AMBGTheme';
// Components
import AMBGDialog from 'shared/components/AMBGDialog/AMBGDialog';
import Notification from './Notification';
import AMBGTooltip from 'shared/components/AMBGToolTip/AMBGToolTip';
// Styles
import * as Sx from './LayoutHeader.style';
// Type/Interface
import { IState } from 'shared/interface/state';
import { IModalState } from 'shared/interface';
import { IRole } from 'features/auth/login/LoginScreen.type';
// Assets
import AMBGLogo from 'assets/images/ambg-logo.png';

type LayoutHeaderProps = {
	isDrawerOpen: boolean;
};
const StyledBadge = styled(Badge)<BadgeProps>(() => ({
	'& .MuiBadge-badge': {
		right: -3,
		top: -1,
		backgroundColor: '#ffffff',
		border: '2px solid #ffffff',
		padding: '0 4px'
	}
}));

const LayoutHeader: FC<LayoutHeaderProps> = (props) => {
	const { isDrawerOpen } = props;
	const dispatch = useDispatch();
	const authData = useSelector((state: IState) => state.auth);
	const role = useSelector((state: IState) => state.auth.role as IRole);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [modalState, setModalState] = useState<IModalState>(INITIAL_MODAL_STATE);
	const [actionLoading, setActionLoading] = useState<boolean>(false);
	const [isOpenDrawer, setIsOpenDrawer] = useState<boolean>(false);
	const [unreadCount, setUnreadCount] = useState<number>(0);
	const navigate = useNavigate();

	const open = Boolean(anchorEl);

	const handleClick = (event: MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleLogout = () => {
		setActionLoading(true);
		dispatch(createAction(actionTypes.AUTH_FAIL));
		dispatch(createAction(actionTypes.CLEAR_DATA));
		setActionLoading(false);
	};

	const previousMonth = moment().subtract(1, 'month').format('MMMM YYYY');
	const timeLeftToSubmit = calculateDayDifference(
		moment().toISOString(),
		moment(authData.sales_end_date).toISOString()
	);
	const salesEndDate = moment(authData.sales_end_date).format('DD MMMM YYYY');
	const isDisplayTimeline = role.role_id === 2 || role.role_id === 4;

	const handleNotificationToggle = () => {
		setIsOpenDrawer(!isOpenDrawer);
	};
	const handleUnreadCount = (count: number) => {
		setUnreadCount(count);
	};

	return (
		<>
			<Box sx={Sx.mainRoot}>
				<Box sx={{ height: '70px' }}>
					{!isDrawerOpen && (
						<img alt='ambg logo' src={AMBGLogo} style={{ mixBlendMode: 'multiply', height: '100%' }} />
					)}
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
					{isDisplayTimeline && (
						<>
							{checkTimeline() ? (
								<Box sx={Sx.timelineRoot} data-testid='timelineRunningInfo'>
									Timeline to submit
									<Typography component='span' sx={Sx.timelineBold}>
										&nbsp;{previousMonth}&nbsp;
									</Typography>
									data :
									<Typography component='span' sx={Sx.timeline}>
										&nbsp;{`${salesEndDate} (${timeLeftToSubmit})`}
									</Typography>
								</Box>
							) : (
								<Box sx={Sx.timelineRoot} data-testid='timelineEndedInfo'>
									Timeline to submit
									<Typography component='span' sx={Sx.timelineBold}>
										&nbsp;{previousMonth}&nbsp;
									</Typography>
									data has ended on
									<Typography component='span' sx={Sx.timelineBold}>
										&nbsp;{salesEndDate}.&nbsp;
									</Typography>
									Please reach out to AMBG team if you need to submit the data for
									<Typography component='span' sx={Sx.timelineBold}>
										&nbsp;{previousMonth}.
									</Typography>
								</Box>
							)}
						</>
					)}
					<AMBGTooltip
						title='Notifications'
						arrow
						PopperProps={{
							modifiers: [
								{
									name: 'offset',
									options: {
										offset: [0, 0]
									}
								}
							]
						}}
					>
						<IconButton
							sx={{ color: 'white' }}
							onClick={() => {
								setIsOpenDrawer(true);
							}}
							data-testid='notificationButton'
						>
							<StyledBadge sx={{ color: colors.primary.main }} badgeContent={unreadCount} max={999}>
								<NotificationsIcon fontSize='medium' sx={{ height: '30px', width: '30px' }} />
							</StyledBadge>
						</IconButton>
					</AMBGTooltip>
					<Box sx={Sx.infoRoot} data-testid='userInfo'>
						<Avatar src={authData.avatar || ''} sx={Sx.avatar} />
						<Box sx={Sx.infoBox} onClick={handleClick} data-testid='nameInfo'>
							<Box sx={Sx.nameBox}>
								<Typography data-testid='userName' sx={Sx.name}>
									{authData.name}
								</Typography>
								<Typography data-testid='companyName' sx={Sx.companyName}>
									{authData.role && authData.role.company_name}
								</Typography>
							</Box>
							{!open ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
						</Box>
					</Box>
				</Box>
				<Menu
					anchorEl={anchorEl}
					data-testid='actionMenu'
					id='account-menu'
					open={open}
					onClick={handleClose}
					PaperProps={paperProps}
				>
					<MenuItem data-testid='myProfileItem' sx={Sx.menuItem} onClick={() => navigate(ROUTES.MY_PROFILE)}>
						<ListItemIcon>
							<ManageAccountsIcon sx={Sx.icon} />
						</ListItemIcon>
						My Profile
					</MenuItem>
					<MenuItem
						data-testid='changePWItem'
						sx={Sx.menuItem}
						onClick={() => navigate(ROUTES.CHANGE_PASSWORD)}
					>
						<ListItemIcon>
							<KeyIcon sx={Sx.icon} />
						</ListItemIcon>
						Change Password
					</MenuItem>
					<MenuItem
						data-testid='actionItem'
						sx={Sx.menuItem}
						onClick={() => setModalState({ open: true, type: Modal.Logout })}
					>
						<ListItemIcon>
							<LogoutIcon sx={Sx.icon} />
						</ListItemIcon>
						Logout
					</MenuItem>
				</Menu>
				{modalState.open && (
					<>
						{modalState.type === Modal.Logout && (
							<AMBGDialog
								loading={actionLoading}
								open={modalState.open}
								maxWidth='xs'
								onConfirm={handleLogout}
								onCancel={() => setModalState(INITIAL_MODAL_STATE)}
							>
								<Typography textAlign='center'>Are you sure you want to Logout ?</Typography>
							</AMBGDialog>
						)}
					</>
				)}
			</Box>
			<Notification
				isOpen={isOpenDrawer}
				handleNotificationToggle={handleNotificationToggle}
				handleUnreadCount={handleUnreadCount}
				unreadCount={unreadCount}
			/>
		</>
	);
};

export default LayoutHeader;

const paperProps = {
	elevation: 0,
	sx: {
		overflow: 'visible',
		filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
		mt: '10px',
		'&:before': {
			content: '""',
			display: 'block',
			position: 'absolute',
			top: 0,
			right: 14,
			width: 10,
			height: 10,
			bgcolor: 'background.paper',
			transform: 'translateY(-50%) rotate(45deg)',
			zIndex: 0
		}
	}
};
